import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import WhatsAppIcon from "@/components/WhatsAppIcon";
import { LOGO_URL, whatsappLink } from "@/config";

const NAV = [
    { label: "Chi siamo", href: "#chi-siamo" },
    { label: "Servizi", href: "#servizi" },
    { label: "FaMa Lab", href: "#fama-lab" },
    { label: "Perché noi", href: "#perche-noi" },
    { label: "Come funziona", href: "#come-funziona" },
    { label: "Contatti", href: "#contatti" },
];

export default function Header() {
    const [scrolled, setScrolled] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 40);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const go = (e, href) => {
        e.preventDefault();
        setMenuOpen(false);
        const el = document.querySelector(href);
        if (el) el.scrollIntoView({ behavior: "smooth" });
    };

    useEffect(() => {
        document.body.style.overflow = menuOpen ? "hidden" : "";
        if (menuOpen) {
            window.__lenis?.stop();
        } else {
            window.__lenis?.start();
        }
        return () => {
            document.body.style.overflow = "";
            window.__lenis?.start();
        };
    }, [menuOpen]);

    return (
        <>
        <motion.header
            initial={{ y: -80, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
            className={`fixed inset-x-0 top-0 z-[9990] transition-[background-color,box-shadow,padding] duration-500 ${
                scrolled
                    ? "bg-[#fdfbf7]/85 py-3 shadow-[0_1px_0_rgba(201,162,39,0.15)] backdrop-blur-xl"
                    : "bg-transparent py-5"
            }`}
            data-testid="site-header"
        >
            <div className="mx-auto flex max-w-7xl items-center justify-between px-5 sm:px-8">
                <a
                    href="#top"
                    onClick={(e) => go(e, "#top")}
                    className="flex items-center"
                    data-testid="header-logo-link"
                >
                    <img
                        src={LOGO_URL}
                        alt="FaMa Apartments"
                        className={`w-auto transition-all duration-500 ${
                            scrolled ? "h-11" : "h-14"
                        }`}
                    />
                </a>

                <nav className="hidden items-center gap-6 xl:gap-9 lg:flex">
                    {NAV.map((n) => (
                        <a
                            key={n.href}
                            href={n.href}
                            onClick={(e) => go(e, n.href)}
                            data-testid={`nav-${n.href.replace("#", "")}`}
                            className="group relative font-body text-sm font-medium text-[var(--fama-ink)]"
                        >
                            {n.label}
                            <span className="absolute -bottom-1 left-0 h-px w-0 bg-[var(--fama-gold)] transition-all duration-300 group-hover:w-full" />
                        </a>
                    ))}
                </nav>

                <div className="flex items-center gap-3">
                    <a
                        href={whatsappLink()}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="header-whatsapp-button"
                        className="hidden items-center gap-2 rounded-full bg-[var(--wa-green)] px-5 py-2.5 font-body text-sm font-semibold text-white transition-colors hover:bg-[var(--wa-green-hover)] sm:flex"
                    >
                        <WhatsAppIcon size={17} /> WhatsApp
                    </a>
                    <button
                        className="lg:hidden text-[var(--fama-ink)]"
                        onClick={() => setMenuOpen(true)}
                        aria-label="Apri menu"
                        data-testid="mobile-menu-open"
                    >
                        <Menu size={26} />
                    </button>
                </div>
            </div>
        </motion.header>

        <AnimatePresence>
            {menuOpen && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    style={{ backgroundColor: "#FDFBF7" }}
                    className="fixed inset-0 z-[10000] flex flex-col overflow-y-auto lg:hidden"
                    data-testid="mobile-menu"
                >
                    <div className="flex items-center justify-between px-5 py-5">
                        <img src={LOGO_URL} alt="FaMa Apartments" className="h-12 w-auto" />
                        <button
                            onClick={() => setMenuOpen(false)}
                            aria-label="Chiudi menu"
                            data-testid="mobile-menu-close"
                            className="text-[var(--fama-ink)]"
                        >
                            <X size={28} />
                        </button>
                    </div>
                    <nav className="flex flex-col gap-2 px-6 pt-6">
                        {NAV.map((n, i) => (
                            <motion.a
                                key={n.href}
                                href={n.href}
                                onClick={(e) => go(e, n.href)}
                                initial={{ opacity: 0, x: -20 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: 0.06 * i }}
                                className="border-b border-[var(--fama-border)] py-4 font-serif-display text-3xl text-[var(--fama-ink)]"
                            >
                                {n.label}
                            </motion.a>
                        ))}
                        <a
                            href={whatsappLink()}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="mt-6 flex items-center justify-center gap-2 rounded-full bg-[var(--wa-green)] px-5 py-4 font-body font-semibold text-white"
                        >
                            <WhatsAppIcon size={20} /> Scrivici su WhatsApp
                        </a>
                    </nav>
                </motion.div>
            )}
        </AnimatePresence>
        </>
    );
}
